import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import PageLayout from "../layout/PageLayout";
import { InvaderService, CatalogInvader, City } from "../../services/InvaderService";
import { GamerService, FlashInvadersProfile } from "../../services/GamerService";
import { InvaderStatus } from "../../constants/InvaderStatus";
import "./InvadersPage.css";

type ViewMode = "compact" | "list";
type StatusFilter = "all" | "captured" | "missing";

const LIST_PAGE_SIZE = 60;

/** Trigramme ville depuis l'id (PA_1234 -> PA). */
const trigramOf = (id: string): string => {
    const idx = id.lastIndexOf("_");
    return idx > 0 ? id.substring(0, idx) : id;
};

/** Numéro dans la ville depuis l'id (PA_1234 -> 1234). */
const numOf = (invader: CatalogInvader): number => {
    if (invader.spaceId != null) return invader.spaceId;
    const idx = invader.id.lastIndexOf("_");
    const n = idx > 0 ? parseInt(invader.id.substring(idx + 1), 10) : NaN;
    return isNaN(n) ? 0 : n;
};

const needsValue = (invader: CatalogInvader): boolean =>
    invader.value == null || invader.value < 0;

type CityGroup = { trigram: string; name: string; invaders: CatalogInvader[] };

/** Regroupe une liste (déjà triée par ville puis numéro) en sections par ville. */
const groupByCity = (list: CatalogInvader[], cityNames: Map<string, string>): CityGroup[] => {
    const groups: CityGroup[] = [];
    list.forEach((inv) => {
        const trigram = trigramOf(inv.id);
        const last = groups[groups.length - 1];
        if (last && last.trigram === trigram) {
            last.invaders.push(inv);
        } else {
            groups.push({ trigram, name: cityNames.get(trigram) ?? trigram, invaders: [inv] });
        }
    });
    return groups;
};

const InvadersPage = () => {
    const [invaders, setInvaders] = useState<CatalogInvader[]>([]);
    const [streaming, setStreaming] = useState(true);
    const [cities, setCities] = useState<City[]>([]);
    const [flashProfile, setFlashProfile] = useState<FlashInvadersProfile | null>(null);
    const [error, setError] = useState<string | null>(null);

    const [view, setView] = useState<ViewMode>("list");
    const [search, setSearch] = useState("");
    const [selectedCity, setSelectedCity] = useState<string>("all");
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
    const [visibleCount, setVisibleCount] = useState(LIST_PAGE_SIZE);

    // Dialog d'action (vue compacte + saisie de points)
    const [dialogInvader, setDialogInvader] = useState<CatalogInvader | null>(null);
    const [points, setPoints] = useState("");
    const [dialogError, setDialogError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const streamStarted = useRef(false);
    const seenIds = useRef(new Set<string>());
    const { getAccessTokenSilently } = useAuth0();
    const navigate = useNavigate();

    useEffect(() => {
        if (streamStarted.current) return;
        streamStarted.current = true;

        InvaderService.fetchCities(getAccessTokenSilently)
            .then(setCities)
            .catch(() => setCities([]));

        GamerService.getMe(getAccessTokenSilently)
            .then((userData) => {
                if (userData?.flashProfile) setFlashProfile(userData.flashProfile);
            })
            .catch(() => null);

        InvaderService.fetchInvadersCatalog(getAccessTokenSilently, (batch) => {
            const fresh = batch.filter((inv) => !seenIds.current.has(inv.id));
            if (fresh.length === 0) return;
            fresh.forEach((inv) => seenIds.current.add(inv.id));
            setInvaders((prev) => [...prev, ...fresh]);
        })
            .catch((err) => {
                console.error("Erreur lors du chargement du catalogue:", err);
                setError("Impossible de charger les invaders");
            })
            .finally(() => setStreaming(false));
    }, [getAccessTokenSilently]);

    const cityNames = useMemo(() => {
        const map = new Map<string, string>();
        cities.forEach((city) => map.set(city.shortName, city.name));
        return map;
    }, [cities]);

    // Compteurs capturés/total par trigramme de ville
    const cityCounts = useMemo(() => {
        const map = new Map<string, { captured: number; total: number }>();
        invaders.forEach((inv) => {
            const trigram = trigramOf(inv.id);
            const counts = map.get(trigram) ?? { captured: 0, total: 0 };
            counts.total++;
            if (inv.captured) counts.captured++;
            map.set(trigram, counts);
        });
        return map;
    }, [invaders]);

    const cityOptions = useMemo(
        () => Array.from(cityCounts.entries())
            .map(([trigram, counts]) => ({
                trigram,
                name: cityNames.get(trigram) ?? trigram,
                ...counts,
            }))
            .sort((a, b) => b.total - a.total),
        [cityCounts, cityNames]
    );

    const filtered = useMemo(() => {
        const query = search.trim().toUpperCase();
        return invaders
            .filter((inv) => {
                if (selectedCity !== "all" && trigramOf(inv.id) !== selectedCity) return false;
                if (statusFilter === "captured" && !inv.captured) return false;
                if (statusFilter === "missing" && inv.captured) return false;
                if (query && !inv.id.toUpperCase().includes(query)) return false;
                return true;
            })
            .sort((a, b) => {
                const cityCompare = trigramOf(a.id).localeCompare(trigramOf(b.id));
                return cityCompare !== 0 ? cityCompare : numOf(a) - numOf(b);
            });
    }, [invaders, selectedCity, statusFilter, search]);

    // Groupes par ville : la vue compacte affiche tout, la vue liste par tranches
    const cityGroups = useMemo(() => groupByCity(filtered, cityNames), [filtered, cityNames]);
    const visibleGroups = useMemo(
        () => groupByCity(filtered.slice(0, visibleCount), cityNames),
        [filtered, visibleCount, cityNames]
    );

    const capturedCount = useMemo(() => invaders.filter((inv) => inv.captured).length, [invaders]);
    const totalPoints = flashProfile?.score
        ?? invaders.reduce((sum, inv) => sum + (inv.captured && inv.value && inv.value > 0 ? inv.value : 0), 0);
    const citiesVisited = flashProfile?.cityFound
        ?? Array.from(cityCounts.values()).filter((counts) => counts.captured > 0).length;

    const updateInvader = (id: string, patch: Partial<CatalogInvader>) => {
        setInvaders((prev) => prev.map((inv) => (inv.id === id ? { ...inv, ...patch } : inv)));
    };

    const captureInvader = async (invader: CatalogInvader, pointsValue?: number) => {
        if (pointsValue != null) {
            await InvaderService.updateValue(getAccessTokenSilently, invader.id, pointsValue);
        }
        await InvaderService.markAsTook(getAccessTokenSilently, invader.id);
        updateInvader(invader.id, {
            captured: true,
            captureSource: "LOCAL",
            value: pointsValue ?? invader.value,
        });
    };

    const removeCapture = async (invader: CatalogInvader) => {
        await InvaderService.removeInvader(getAccessTokenSilently, invader.id);
        updateInvader(invader.id, { captured: false, captureSource: null });
    };

    const openDialog = (invader: CatalogInvader) => {
        setDialogInvader(invader);
        setPoints("");
        setDialogError("");
    };

    const closeDialog = () => {
        if (!submitting) {
            setDialogInvader(null);
            setPoints("");
            setDialogError("");
        }
    };

    // ⚡ depuis la vue liste : dialog seulement si les points sont inconnus
    const handleFlashClick = async (invader: CatalogInvader) => {
        if (needsValue(invader)) {
            openDialog(invader);
            return;
        }
        try {
            await captureInvader(invader);
        } catch (err) {
            console.error("Erreur lors de la capture:", err);
            setError("Erreur lors de la capture. Veuillez réessayer.");
        }
    };

    const handleRemoveClick = async (invader: CatalogInvader) => {
        try {
            await removeCapture(invader);
        } catch (err) {
            console.error("Erreur lors du retrait:", err);
            setError("Erreur lors du retrait. Veuillez réessayer.");
        }
    };

    const handleDialogCapture = async () => {
        if (!dialogInvader) return;
        let pointsValue: number | undefined;
        if (needsValue(dialogInvader)) {
            pointsValue = parseInt(points, 10);
            if (isNaN(pointsValue) || pointsValue < 10 || pointsValue > 100) {
                setDialogError("Les points doivent être entre 10 et 100");
                return;
            }
        }
        setSubmitting(true);
        setDialogError("");
        try {
            await captureInvader(dialogInvader, pointsValue);
            setDialogInvader(null);
        } catch (err) {
            console.error("Erreur lors de la capture:", err);
            setDialogError("Une erreur est survenue. Veuillez réessayer.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDialogRemove = async () => {
        if (!dialogInvader) return;
        setSubmitting(true);
        setDialogError("");
        try {
            await removeCapture(dialogInvader);
            setDialogInvader(null);
        } catch (err) {
            console.error("Erreur lors du retrait:", err);
            setDialogError("Une erreur est survenue. Veuillez réessayer.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PageLayout>
            <div className="invaders-container">
                {/* Terminal Window Header */}
                <div className="invaders-terminal">
                    <div className="invaders-terminal__header">
                        <div className="invaders-terminal__dots">
                            <span className="invaders-terminal__dot invaders-terminal__dot--red"></span>
                            <span className="invaders-terminal__dot invaders-terminal__dot--yellow"></span>
                            <span className="invaders-terminal__dot invaders-terminal__dot--green"></span>
                        </div>
                        <span className="invaders-terminal__title">invaders</span>
                    </div>
                    <div className="invaders-terminal__body">
                        <h1 className="invaders-title">Invaders<span className="cursor-blink"></span></h1>
                        <p className="invaders-subtitle">
                            {flashProfile ? `${flashProfile.pseudo} - ` : ""}
                            Tout le catalogue : sélectionnez ceux que vous avez flashés
                        </p>
                    </div>
                </div>

                {/* Stats */}
                <div className="invaders-stats">
                    <div className="invaders-stat">
                        <span className="invaders-stat__value">
                            {capturedCount}<span className="invaders-stat__total">/{invaders.length}</span>
                        </span>
                        <span className="invaders-stat__label">Capturés</span>
                    </div>
                    <div className="invaders-stat">
                        <span className="invaders-stat__value invaders-stat__value--gold">{totalPoints}</span>
                        <span className="invaders-stat__label">Points</span>
                    </div>
                    <div className="invaders-stat">
                        <span className="invaders-stat__value invaders-stat__value--green">{citiesVisited}</span>
                        <span className="invaders-stat__label">Villes</span>
                    </div>
                </div>

                {/* Toolbar */}
                <div className="invaders-toolbar">
                    <div className="invaders-view-toggle">
                        <button
                            className={`invaders-view-toggle__button ${view === "compact" ? "invaders-view-toggle__button--active" : ""}`}
                            onClick={() => setView("compact")}
                            title="Vue compacte"
                        >
                            ⊞ Compact
                        </button>
                        <button
                            className={`invaders-view-toggle__button ${view === "list" ? "invaders-view-toggle__button--active" : ""}`}
                            onClick={() => { setView("list"); setVisibleCount(LIST_PAGE_SIZE); }}
                            title="Vue liste"
                        >
                            ☰ Liste
                        </button>
                    </div>
                    <input
                        type="text"
                        className="invaders-search"
                        placeholder="Rechercher (ex: PA_1234)"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value.toUpperCase()); setVisibleCount(LIST_PAGE_SIZE); }}
                    />
                    <select
                        className="invaders-select"
                        value={selectedCity}
                        onChange={(e) => { setSelectedCity(e.target.value); setVisibleCount(LIST_PAGE_SIZE); }}
                    >
                        <option value="all">Toutes les villes</option>
                        {cityOptions.map((city) => (
                            <option key={city.trigram} value={city.trigram}>
                                {city.name} ({city.captured}/{city.total})
                            </option>
                        ))}
                    </select>
                    <select
                        className="invaders-select"
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value as StatusFilter); setVisibleCount(LIST_PAGE_SIZE); }}
                    >
                        <option value="all">Tous</option>
                        <option value="captured">Capturés</option>
                        <option value="missing">Manquants</option>
                    </select>
                </div>

                {/* Progression du chargement */}
                {streaming && (
                    <p className="invaders-streaming">
                        <span className="invaders-streaming__dot"></span>
                        Chargement du catalogue... {invaders.length} invaders
                    </p>
                )}

                {error && (
                    <div className="invaders-alert">
                        <span>{error}</span>
                        <button onClick={() => setError(null)}>×</button>
                    </div>
                )}

                {/* Vue compacte : damier groupé par ville */}
                {view === "compact" && (
                    <div className="invaders-compact">
                        {cityGroups.map((group) => (
                            <section key={group.trigram} className="invaders-group">
                                <h3 className="invaders-group__title">
                                    {group.name}
                                    <span className="invaders-group__count">
                                        {group.invaders.filter((inv) => inv.captured).length}/{group.invaders.length}
                                    </span>
                                </h3>
                                <div className="invaders-compact__grid">
                                    {group.invaders.map((inv) => (
                                        <button
                                            key={inv.id}
                                            className={[
                                                "invaders-cell",
                                                inv.captured ? "invaders-cell--captured" : "",
                                                inv.status === InvaderStatus.DESTROYED ? "invaders-cell--destroyed" : "",
                                            ].join(" ").trim()}
                                            onClick={() => openDialog(inv)}
                                            title={`${inv.id}${inv.value && inv.value > 0 ? ` — ${inv.value} pts` : ""}`}
                                        >
                                            {numOf(inv)}
                                        </button>
                                    ))}
                                </div>
                            </section>
                        ))}
                    </div>
                )}

                {/* Vue liste : cartes avec image, groupées par ville */}
                {view === "list" && (
                    <>
                        {visibleGroups.map((group) => (
                            <section key={group.trigram} className="invaders-group">
                                <h3 className="invaders-group__title">
                                    {group.name}
                                    <span className="invaders-group__count">
                                        {(cityCounts.get(group.trigram)?.captured ?? 0)}/{(cityCounts.get(group.trigram)?.total ?? group.invaders.length)}
                                    </span>
                                </h3>
                                <div className="invaders-grid">
                                    {group.invaders.map((inv) => (
                                <div
                                    key={inv.id}
                                    className={`invaders-card ${inv.status === InvaderStatus.DESTROYED ? "invaders-card--destroyed" : ""} ${!inv.captured ? "invaders-card--missing" : ""}`}
                                >
                                    <div
                                        className="invaders-card__image-container"
                                        onClick={() => navigate(`/invaders/${inv.id}`)}
                                        role="button"
                                        title="Voir le détail"
                                    >
                                        <img
                                            src={inv.imageUrl || "/invader.svg"}
                                            alt={inv.id}
                                            className="invaders-card__image"
                                            loading="lazy"
                                            onError={(e) => {
                                                (e.target as HTMLImageElement).src = "/invader.svg";
                                            }}
                                        />
                                        <span className="invaders-card__name">{inv.id}</span>
                                        {inv.value != null && inv.value > 0 && (
                                            <span className="invaders-card__points">{inv.value} pts</span>
                                        )}
                                        {inv.captured && <span className="invaders-card__captured-badge">✓</span>}
                                    </div>
                                    <div className="invaders-card__actions">
                                        {!inv.captured && (
                                            <button
                                                className="invaders-card__action invaders-card__action--flash"
                                                onClick={() => handleFlashClick(inv)}
                                                title="Marquer comme capturé"
                                            >
                                                ⚡
                                            </button>
                                        )}
                                        {inv.captured && inv.captureSource === "LOCAL" && (
                                            <button
                                                className="invaders-card__action invaders-card__action--delete"
                                                onClick={() => handleRemoveClick(inv)}
                                                title="Retirer de mes captures"
                                            >
                                                🗑️
                                            </button>
                                        )}
                                        {inv.captured && inv.captureSource === "FLASH_INVADERS" && (
                                            <span className="invaders-card__action invaders-card__action--locked" title="Capture Flash Invaders (non modifiable)">
                                                🔒
                                            </span>
                                        )}
                                    </div>
                                </div>
                                    ))}
                                </div>
                            </section>
                        ))}
                        {filtered.length > visibleCount && (
                            <button
                                className="invaders-load-more"
                                onClick={() => setVisibleCount((count) => count + 2 * LIST_PAGE_SIZE)}
                            >
                                Afficher plus ({filtered.length - visibleCount} restants)
                            </button>
                        )}
                    </>
                )}

                {!streaming && filtered.length === 0 && (
                    <div className="invaders-empty">
                        <span className="invaders-empty__icon">👾</span>
                        <p>Aucun invader ne correspond à ces filtres</p>
                    </div>
                )}

                {/* Dialog d'action */}
                <Dialog open={dialogInvader !== null} onClose={closeDialog} maxWidth="xs" fullWidth>
                    {dialogInvader && (
                        <>
                            <DialogTitle sx={{ backgroundColor: "#1a1a2e", color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
                                <span role="img" aria-label="invader">👾</span>
                                {dialogInvader.id}
                            </DialogTitle>
                            <DialogContent sx={{ mt: 2 }}>
                                <p style={{ color: "#333", marginBottom: "8px" }}>
                                    {cityNames.get(trigramOf(dialogInvader.id)) ?? trigramOf(dialogInvader.id)}
                                    {dialogInvader.value != null && dialogInvader.value > 0 && ` — ${dialogInvader.value} pts`}
                                    {dialogInvader.captured
                                        ? ` — capturé${dialogInvader.captureSource === "FLASH_INVADERS" ? " (Flash Invaders)" : ""}`
                                        : " — non capturé"}
                                </p>
                                {!dialogInvader.captured && needsValue(dialogInvader) && (
                                    <TextField
                                        autoFocus
                                        fullWidth
                                        type="number"
                                        label="Points"
                                        placeholder="Ex: 10, 20, 50, 100..."
                                        value={points}
                                        onChange={(e) => setPoints(e.target.value)}
                                        disabled={submitting}
                                        error={!!dialogError}
                                        helperText={dialogError || "Valeur entre 10 et 100"}
                                        inputProps={{ min: 10, max: 100, step: 10 }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && !submitting) handleDialogCapture();
                                        }}
                                    />
                                )}
                                {dialogError && (dialogInvader.captured || !needsValue(dialogInvader)) && (
                                    <p style={{ color: "#ef4444" }}>{dialogError}</p>
                                )}
                            </DialogContent>
                            <DialogActions sx={{ padding: "16px 24px", flexWrap: "wrap", gap: 1 }}>
                                <Button onClick={() => navigate(`/invaders/${dialogInvader.id}`)} color="inherit">
                                    Voir le détail
                                </Button>
                                {!dialogInvader.captured && (
                                    <Button
                                        onClick={handleDialogCapture}
                                        disabled={submitting}
                                        variant="contained"
                                        sx={{ backgroundColor: "#00d4ff", "&:hover": { backgroundColor: "#00b8e6" } }}
                                    >
                                        {submitting ? <CircularProgress size={24} color="inherit" /> : "⚡ Flasher"}
                                    </Button>
                                )}
                                {dialogInvader.captured && dialogInvader.captureSource === "LOCAL" && (
                                    <Button onClick={handleDialogRemove} disabled={submitting} color="error">
                                        {submitting ? <CircularProgress size={24} color="inherit" /> : "Retirer"}
                                    </Button>
                                )}
                            </DialogActions>
                        </>
                    )}
                </Dialog>
            </div>
        </PageLayout>
    );
};

export default InvadersPage;
